import { useState, useEffect, useRef } from 'react';
import { reservationAPI } from '../api/axios';
import Layout from '../components/Layout';
import Button from '../components/Button';
import './ReservationCancelPage.css';

const MOBILE_BREAKPOINT = 768;

const ReservationCancelPage = () => {
  // 반응형 상태
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  const [step, setStep] = useState(1); // 1: 전화번호, 2: 예약목록, 3: 완료
  const [phone, setPhone] = useState('');
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [slideDirection, setSlideDirection] = useState('next');
  const stepContainerRef = useRef(null);

  // 반응형 리사이즈 핸들러
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(newIsMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 단계 전환 시 스크롤 상단 이동
  useEffect(() => {
    if (stepContainerRef.current) {
      stepContainerRef.current.scrollTop = 0;
    }
  }, [step]);

  // 전화번호 포맷팅
  const formatPhone = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  // 전화번호 입력 핸들러
  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    setError('');
  };

  // 전화번호로 예약 조회
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();

    if (!phone || phone.length < 12) {
      setError('올바른 전화번호를 입력해주세요');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await reservationAPI.getMyReservations(phone);
      setReservations(response.data);

      if (response.data.length === 0) {
        setError('예약 내역이 없습니다');
      } else {
        setSlideDirection('next');
        setStep(2);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('해당 전화번호로 등록된 예약이 없습니다');
      } else {
        setError(err.response?.data?.message || '예약 조회 중 오류가 발생했습니다');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 예약 취소
  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('정말 예약을 취소하시겠습니까?\n\n취소된 예약은 복구할 수 없습니다.')) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await reservationAPI.selfCancel(reservationId, { phone });
      setSlideDirection('next');
      setStep(3);
    } catch (err) {
      alert(err.response?.data?.message || '예약 취소 중 오류가 발생했습니다');

      // 48시간 이내 등의 오류 시 예약 목록 다시 조회
      try {
        const response = await reservationAPI.getMyReservations(phone);
        setReservations(response.data);
      } catch (refreshErr) {
        console.error('예약 목록 갱신 실패:', refreshErr);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 예약 시간 포맷팅
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const dayName = dayNames[date.getDay()];

    return `${year}년 ${month}월 ${day}일 (${dayName}) ${hours}:${minutes}`;
  };

  // 예약 상태 한글 변환
  const getStatusText = (status) => {
    const statusMap = {
      'PENDING': '확정대기',
      'CONFIRMED': '확정',
      'COMPLETED': '완료',
      'CANCELED': '취소됨',
      'NO_SHOW': '노쇼'
    };
    return statusMap[status] || status;
  };

  // 예약 상태 배지 색상
  const getStatusBadgeClass = (status) => {
    const classMap = {
      'PENDING': 'status-pending',
      'CONFIRMED': 'status-confirmed',
      'COMPLETED': 'status-completed',
      'CANCELED': 'status-canceled',
      'NO_SHOW': 'status-noshow'
    };
    return classMap[status] || 'status-default';
  };

  // 처음으로 돌아가기
  const handleReset = () => {
    setSlideDirection('prev');
    setStep(1);
    setPhone('');
    setReservations([]);
    setError('');
  };

  // 이전 단계로 이동
  const handlePrevStep = () => {
    setSlideDirection('prev');
    setStep(1);
  };

  // Enter 키 처리
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handlePhoneSubmit(e);
    }
  };

  // ========== 모바일 렌더링 ==========
  const renderMobileProgress = () => (
    <div className="mobile-progress">
      <div className="mobile-progress-bar">
        <div
          className="mobile-progress-fill"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>
      <div className="mobile-progress-text">
        <span className="mobile-progress-current">{step}</span>
        <span className="mobile-progress-divider">/</span>
        <span className="mobile-progress-total">3</span>
      </div>
    </div>
  );

  // 모바일 Step 1: 전화번호 입력
  const renderMobilePhoneStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">예약 조회</h2>
        <p className="mobile-step-description">
          예약하신 전화번호를 입력해주세요
        </p>
      </div>

      <div className="mobile-form-section">
        <form onSubmit={handlePhoneSubmit}>
          <div className="input-wrapper">
            <label htmlFor="phone" className="input-label">
              전화번호
              <span className="input-required">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              className={`input-field mobile-input ${error ? 'error' : ''}`}
              placeholder="010-0000-0000"
              value={phone}
              onChange={handlePhoneChange}
              onKeyDown={handleKeyDown}
              maxLength="13"
              autoFocus
            />
            {error && <span className="input-error-message">{error}</span>}
          </div>

          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !phone}
          >
            {isLoading ? '조회 중...' : '예약 조회'}
          </Button>
        </form>

        <div className="cancel-page-info">
          <p className="info-text">
            <span className="info-icon">💡</span>
            예약 취소는 예약일 48시간 전까지 가능합니다
          </p>
        </div>
      </div>
    </div>
  );

  // 모바일 Step 2: 예약 목록
  const renderMobileReservationList = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">예약 내역</h2>
        <p className="mobile-step-description">
          취소할 예약을 선택해주세요
        </p>
      </div>

      <div className="phone-display">
        <span className="phone-label">전화번호</span>
        <span className="phone-number">{phone}</span>
      </div>

      <div className="mobile-reservation-list">
        {reservations.map((reservation) => (
          <div key={reservation.id} className="reservation-card">
            <div className="reservation-card-header">
              <h3 className="reservation-treatment">{reservation.treatment}</h3>
              <span className={`status-badge ${getStatusBadgeClass(reservation.status)}`}>
                {getStatusText(reservation.status)}
              </span>
            </div>

            <div className="reservation-card-body">
              <div className="reservation-info-row">
                <span className="info-icon">📅</span>
                <span className="info-text">{formatDateTime(reservation.reservationTime)}</span>
              </div>
              <div className="reservation-info-row">
                <span className="info-icon">👤</span>
                <span className="info-text">{reservation.customerName}</span>
              </div>
            </div>

            <div className="reservation-card-footer">
              {reservation.cancellable ? (
                <Button
                  variant="danger"
                  size="medium"
                  fullWidth
                  onClick={() => handleCancelReservation(reservation.id)}
                  disabled={isLoading}
                >
                  {isLoading ? '취소 중...' : '예약 취소'}
                </Button>
              ) : (
                <div className="cancel-unavailable">
                  <Button variant="secondary" size="medium" fullWidth disabled>
                    취소 불가
                  </Button>
                  {reservation.cancellableMessage && (
                    <p className="cancel-message">{reservation.cancellableMessage}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {reservations.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <p className="empty-text">예약 내역이 없습니다</p>
          </div>
        )}
      </div>
    </div>
  );

  // 모바일 Step 3: 완료
  const renderMobileCompletionStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="completion-container">
        <div className="completion-header">
          <div className="completion-icon">✅</div>
          <h2 className="completion-title">예약이 취소되었습니다</h2>
          <p className="completion-subtitle">
            예약이 성공적으로 취소되었습니다.<br />
            더 나은 서비스로 다시 찾아뵙겠습니다.
          </p>
        </div>

        <div className="completion-actions">
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={handleReset}
          >
            다른 예약 조회
          </Button>
          <a href="/" className="back-link">메인으로 돌아가기</a>
        </div>
      </div>
    </div>
  );

  // 모바일 하단 버튼
  const renderMobileBottomNav = () => {
    if (step === 3) return null;

    return (
      <div className="mobile-bottom-nav">
        {step === 2 && (
          <Button
            type="button"
            variant="outline"
            size="large"
            onClick={handlePrevStep}
          >
            이전
          </Button>
        )}
      </div>
    );
  };

  const renderMobileView = () => (
    <div className="cancel-page mobile-view">
      {renderMobileProgress()}

      <div className="mobile-step-container" ref={stepContainerRef}>
        {step === 1 && renderMobilePhoneStep()}
        {step === 2 && renderMobileReservationList()}
        {step === 3 && renderMobileCompletionStep()}
      </div>

      {renderMobileBottomNav()}
    </div>
  );

  // ========== 데스크톱 렌더링 ==========
  const renderDesktopView = () => (
    <div className="cancel-page desktop-view">
      {/* Progress Indicator */}
      <div className="progress-indicator">
        <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="progress-circle">{step > 1 ? '✓' : '1'}</div>
          <span className="progress-label">전화번호 입력</span>
        </div>
        <div className={`progress-line ${step > 1 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="progress-circle">{step > 2 ? '✓' : '2'}</div>
          <span className="progress-label">예약 선택</span>
        </div>
        <div className={`progress-line ${step > 2 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="progress-circle">3</div>
          <span className="progress-label">취소 완료</span>
        </div>
      </div>

      {/* Step 1: 전화번호 입력 */}
      {step === 1 && (
        <div className="step-container">
          <div className="step-header">
            <h2 className="step-title">예약 조회 및 취소</h2>
            <p className="step-description">
              예약하신 전화번호를 입력하시면 예약 내역을 확인하실 수 있습니다
            </p>
          </div>

          <div className="desktop-form-card">
            <form onSubmit={handlePhoneSubmit}>
              <div className="input-wrapper">
                <label htmlFor="phone" className="input-label">
                  전화번호
                  <span className="input-required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  className={`input-field ${error ? 'error' : ''}`}
                  placeholder="010-0000-0000"
                  value={phone}
                  onChange={handlePhoneChange}
                  onKeyDown={handleKeyDown}
                  maxLength="13"
                  autoFocus
                />
                {error && <span className="input-error-message">{error}</span>}
              </div>

              <div className="step-actions">
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  loading={isLoading}
                  disabled={isLoading || !phone}
                >
                  {isLoading ? '조회 중...' : '예약 조회'}
                </Button>
              </div>
            </form>

            <div className="cancel-info-box">
              <div className="info-icon-large">💡</div>
              <div className="info-content">
                <h4>예약 취소 안내</h4>
                <ul>
                  <li>예약일 48시간 전까지 무료 취소 가능합니다</li>
                  <li>48시간 이내 취소 시 예약금이 환불되지 않습니다</li>
                  <li>문의사항은 010-7761-5730으로 연락주세요</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: 예약 목록 */}
      {step === 2 && (
        <div className="step-container">
          <div className="step-header">
            <h2 className="step-title">예약 내역</h2>
            <p className="step-description">
              취소하실 예약을 선택해주세요
            </p>
          </div>

          <div className="phone-summary">
            <span className="summary-label">전화번호</span>
            <span className="summary-value">{phone}</span>
            <Button
              type="button"
              variant="outline"
              size="small"
              onClick={handlePrevStep}
            >
              변경
            </Button>
          </div>

          <div className="desktop-reservation-list">
            {reservations.map((reservation) => (
              <div key={reservation.id} className="reservation-card desktop">
                <div className="reservation-card-content">
                  <div className="reservation-card-left">
                    <div className="reservation-card-header">
                      <h3 className="reservation-treatment">{reservation.treatment}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(reservation.status)}`}>
                        {getStatusText(reservation.status)}
                      </span>
                    </div>

                    <div className="reservation-card-body">
                      <div className="reservation-info-row">
                        <span className="info-icon">📅</span>
                        <span className="info-text">{formatDateTime(reservation.reservationTime)}</span>
                      </div>
                      <div className="reservation-info-row">
                        <span className="info-icon">👤</span>
                        <span className="info-text">{reservation.customerName}</span>
                      </div>
                    </div>
                  </div>

                  <div className="reservation-card-right">
                    {reservation.cancellable ? (
                      <Button
                        variant="danger"
                        size="medium"
                        onClick={() => handleCancelReservation(reservation.id)}
                        disabled={isLoading}
                      >
                        {isLoading ? '취소 중...' : '예약 취소'}
                      </Button>
                    ) : (
                      <div className="cancel-unavailable">
                        <Button variant="secondary" size="medium" disabled>
                          취소 불가
                        </Button>
                        {reservation.cancellableMessage && (
                          <p className="cancel-message">{reservation.cancellableMessage}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {reservations.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📅</div>
                <p className="empty-text">예약 내역이 없습니다</p>
              </div>
            )}
          </div>

          <div className="step-actions-group">
            <Button
              type="button"
              variant="outline"
              size="large"
              onClick={handlePrevStep}
            >
              이전
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: 완료 */}
      {step === 3 && (
        <div className="step-container completion-container">
          <div className="completion-header">
            <div className="completion-icon">✅</div>
            <h2 className="completion-title">예약이 취소되었습니다</h2>
            <p className="completion-subtitle">
              예약이 성공적으로 취소되었습니다.<br />
              더 나은 서비스로 다시 찾아뵙겠습니다.
            </p>
          </div>

          <div className="completion-actions">
            <Button
              variant="primary"
              size="large"
              onClick={handleReset}
              style={{ marginBottom: '12px' }}
            >
              다른 예약 조회
            </Button>
            <a href="/" className="back-link">메인으로 돌아가기</a>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      {isMobile ? renderMobileView() : renderDesktopView()}
    </Layout>
  );
};

export default ReservationCancelPage;
